import {
  connect as connectWebsocket,
  disconnect as disconnectWebsocket,
  send as sendWebsocket,
} from "@giantmachines/redux-websocket";

import store from "../store/store";

import { buildSocketUrl } from "./socketConfig";
import { Prefixs } from "./socketPrefix";

export const newSocket = (url, prefix = null) => {
  return new Socket(url, prefix);
};

export const newSocketDefault = (prefix = null) => {
  return newSocket(buildSocketUrl(), prefix);
};

export const newSocketCustom = (endpoint, prefix = null) => {
  return newSocket(buildSocketUrl(endpoint), prefix);
};

export class Socket {
  static #KEEP_ME_ALIVE_INTERVAL_TIME = 45 * 1000;

  #onConnectedHandlers = new Set();
  #onReconnectedHandlers = new Set();

  #isListenerStarted = false;

  #unsubscriber;

  #SOCKET_PREFIX;

  #lastState = {};

  constructor(url, prefix) {
    this.url = url;
    this.#SOCKET_PREFIX = prefix || Prefixs.DEFAULT_PREFIX;
    this.#startListener();
  }

  #startListener = () => {
    if (this.#isListenerStarted) return;

    this.#isListenerStarted = true;

    this.#unsubscriber = store.subscribe(this.#socketListener);
  };

  #stopListenr = () => {
    this.#unsubscriber && this.#unsubscriber();
    this.#isListenerStarted = false;
  };

  #notifyOnConnectedHandlers = () => {
    this.#onConnectedHandlers.forEach((handler) => handler());
    this.#onConnectedHandlers.clear();
  };

  #notifyOnReconnectedHandlers = () => {
    Array.from(this.#onReconnectedHandlers)
      .reverse()
      .forEach((handler) => handler());
  };

  #socketListener = () => {
    const socketsState = store.getState().socket;

    const socketState = socketsState[this.#SOCKET_PREFIX];

    if (!socketState || !socketState.stateChanged) return;

    this.#lastState = socketState;

    if (socketState.connected) {
      this.#startKeepMeAlive();
      this.#notifyOnConnectedHandlers();
    } else if (socketState.reconnected) {
      this.#notifyOnReconnectedHandlers();
    } else if (socketState.disconnected) {
      this.#stopKeepMeAlive();
    }
  };

  isConnected = () => {
    const socketState = store.getState().socket;
    return socketState && socketState[this.#SOCKET_PREFIX]?.connected;
  };

  // Add timer to send message to socket to avoid the disconnecting.
  #intervalId = null;
  #startKeepMeAlive = () => {
    if (this.#intervalId)
      // already set
      return;
    this.#intervalId = setInterval(() => {
      this.isConnected && store.dispatch(this.send({ action: "keep me alive" }));
    }, Socket.#KEEP_ME_ALIVE_INTERVAL_TIME);
  };

  #stopKeepMeAlive = () => {
    clearInterval(this.#intervalId);
    this.#intervalId = null;
  };

  /**
   *
   * @param {*} onConnected connected listener, called only once
   * @param {*} onReconnected optional reconnected listener, called every time that socket reconnected
   */
  connect = (onConnected = null, onReconnected = null) => {
    this.#startListener();

    return (dispatch) => {
      onConnected && this.#onConnectedHandlers.add(onConnected);

      onReconnected && this.#onReconnectedHandlers.add(onReconnected);
      dispatch(connectWebsocket(this.url, this.#SOCKET_PREFIX));
    };
  };

  disconnect = () => {
    return (dispatch) => {
      dispatch(disconnectWebsocket(this.#SOCKET_PREFIX));
    };
  };

  send = (message) => {
    return (dispatch) => {
      dispatch(sendWebsocket(message, this.#SOCKET_PREFIX));
    };
  };

  addOnReconnectedListener = (onReconnected) => {
    this.#onReconnectedHandlers.add(onReconnected);
  };

  clearReconnectedListener = () => {
    this.#onReconnectedHandlers.clear();
  };
}
